import { CheckMarkIcon } from '@/common/icons/arrows.tsx'
import { cn } from '@/common/utilities.ts'
import { useTranslation } from 'react-i18next'

type StepWithoutChildren = {
  name: string
  title_translation_key: string
  location: string
  childPointer?: undefined
  code: string;
  children?: undefined
}

type StepWithChildren = {
  name: string
  title_translation_key: string
  location: string
  childPointer: number
  code: string;
  children?: {
    index: number
    name: string
  }[]
}

export type ApplicationStep = StepWithoutChildren | StepWithChildren

type ApplicationStepperProps = {
  currentStepIndex: number
  readonly steps: ApplicationStep[]
  className?: string
  completeSteps?: string[]
}

const ApplicationStepper = ({
  currentStepIndex,
  steps,
  completeSteps,
  className,
}: ApplicationStepperProps) => {
  const { t: translate } = useTranslation()

  if (currentStepIndex == -1) {
    currentStepIndex = 99;
  }

  return (
    <>
      <nav aria-label="Progress" className={className}>
        <ol
          role="list"
          className="overflow-hidden flex space-x-6 py-0 lg:px-4 px-4 almost_lg:space-x-8"
        >
          {steps.map((step, index) => (
            <li key={step.name} className="relative flex items-center">
              {index < steps.length - 1 && (
                <div
                  aria-hidden="true"
                  className={cn(
                    'absolute top-1/2 left-full h-0.5 w-8 sm:w-6 bg-gray-900 transform sm:ml-2',
                    { 'opacity-50 bg-green-900': index < currentStepIndex }
                  )}
                />
              )}
              <span className="group relative flex items-center">
                <span className="flex items-center justify-center">
                  {(completeSteps?.includes(step.code ?? '')) ? (
                    <span className="ml-2 relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-green-700 text-white opacity-50">
                      <CheckMarkIcon className="text-lg" />
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'ml-2 relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2',
                        index === currentStepIndex
                          ? 'border-green-600'
                          : 'border-gray-500 text-gray-500'
                      )}
                    >
                      <span>{index + 1}</span>
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'ml-2 text-sm font-medium hidden sm:block',
                    index < currentStepIndex
                      ? 'text-gray-900 opacity-50'
                      : 'text-gray-500'
                  )}
                >
                  {translate(step.title_translation_key)}
                </span>
              </span>
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}

export default ApplicationStepper
